/**
 * IndexedDB wrapper for Email App persistent storage
 * Provides fast, offline-capable email caching
 */
import { openDB } from 'idb';
// =====================================================
// DATABASE CLASS
// =====================================================
const DB_NAME = 'EmailAppDB';
const DB_VERSION = 1;
class EmailDB {
    db = null;
    initPromise = null;
    /**
     * Initialize the database and create object stores
     */
    async init() {
        // Return existing init promise if already initializing
        if (this.initPromise) {
            return this.initPromise;
        }
        this.initPromise = (async () => {
            try {
                this.db = await openDB(DB_NAME, DB_VERSION, {
                    upgrade(db, oldVersion, newVersion, transaction) {
                        console.log(`Upgrading EmailDB from v${oldVersion} to v${newVersion}`);
                        // Create emails store
                        if (!db.objectStoreNames.contains('emails')) {
                            const emailStore = db.createObjectStore('emails', { keyPath: 'id' });
                            emailStore.createIndex('by-folder', 'folder');
                            emailStore.createIndex('by-date', 'date');
                            emailStore.createIndex('by-thread', 'threadId');
                            emailStore.createIndex('by-folder-date', ['folder', 'date']);
                        }
                        // Create folders store
                        if (!db.objectStoreNames.contains('folders')) {
                            db.createObjectStore('folders', { keyPath: 'id' });
                        }
                        // Create drafts store
                        if (!db.objectStoreNames.contains('drafts')) {
                            const draftStore = db.createObjectStore('drafts', { keyPath: 'id' });
                            draftStore.createIndex('by-modified', 'modifiedAt');
                        }
                        // Create sync_state store
                        if (!db.objectStoreNames.contains('sync_state')) {
                            db.createObjectStore('sync_state', { keyPath: 'folder' });
                        }
                        // Create contacts store
                        if (!db.objectStoreNames.contains('contacts')) {
                            const contactStore = db.createObjectStore('contacts', { keyPath: 'email' });
                            contactStore.createIndex('by-last-contacted', 'lastContactedAt');
                            contactStore.createIndex('by-email-count', 'emailCount');
                        }
                    },
                    blocked() {
                        console.warn('EmailDB upgrade blocked - close other tabs');
                    },
                    blocking() {
                        console.warn('EmailDB blocking upgrade in other tabs');
                    },
                });
                console.log('EmailDB initialized successfully');
            }
            catch (error) {
                console.error('Failed to initialize EmailDB:', error);
                throw error;
            }
        })();
        return this.initPromise;
    }
    /**
     * Ensure database is initialized before operations
     */
    async ensureDB() {
        if (!this.db) {
            await this.init();
        }
        if (!this.db) {
            throw new Error('Failed to initialize EmailDB');
        }
        return this.db;
    }
    // =====================================================
    // EMAIL OPERATIONS
    // =====================================================
    /**
     * Store a single email
     */
    async putEmail(email) {
        const db = await this.ensureDB();
        await db.put('emails', {
            ...email,
            syncedAt: Date.now(),
        });
    }
    /**
     * Store multiple emails (bulk insert)
     */
    async putEmails(emails) {
        const db = await this.ensureDB();
        const tx = db.transaction('emails', 'readwrite');
        const now = Date.now();
        await Promise.all([
            ...emails.map(email => tx.store.put({
                ...email,
                syncedAt: now,
            })),
            tx.done,
        ]);
    }
    /**
     * Get all emails for a specific folder
     */
    async getEmailsByFolder(folder) {
        const db = await this.ensureDB();
        return db.getAllFromIndex('emails', 'by-folder', folder);
    }
    /**
     * Get emails for a folder sorted by date (newest first)
     */
    async getEmailsByFolderSorted(folder, limit) {
        const db = await this.ensureDB();
        const emails = await db.getAllFromIndex('emails', 'by-folder', folder);
        // Sort by date descending
        emails.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        if (limit) {
            return emails.slice(0, limit);
        }
        return emails;
    }
    /**
     * Get a single email by ID
     */
    async getEmail(id) {
        const db = await this.ensureDB();
        return db.get('emails', id);
    }
    /**
     * Get emails by thread ID
     */
    async getEmailsByThread(threadId) {
        const db = await this.ensureDB();
        const emails = await db.getAllFromIndex('emails', 'by-thread', threadId);
        // Sort by date ascending for thread view
        emails.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        return emails;
    }
    /**
     * Update email flags (read, starred, etc.)
     */
    async updateEmail(id, updates) {
        const db = await this.ensureDB();
        const email = await db.get('emails', id);
        if (!email) {
            throw new Error(`Email not found: ${id}`);
        }
        await db.put('emails', { ...email, ...updates });
    }
    /**
     * Delete an email
     */
    async deleteEmail(id) {
        const db = await this.ensureDB();
        await db.delete('emails', id);
    }
    /**
     * Delete all emails in a folder
     */
    async deleteEmailsByFolder(folder) {
        const db = await this.ensureDB();
        const emails = await this.getEmailsByFolder(folder);
        const tx = db.transaction('emails', 'readwrite');
        await Promise.all([
            ...emails.map(email => tx.store.delete(email.id)),
            tx.done,
        ]);
    }
    /**
     * Get count of emails in a folder
     */
    async getEmailCount(folder) {
        const db = await this.ensureDB();
        return db.countFromIndex('emails', 'by-folder', folder);
    }
    /**
     * Get count of unread emails in a folder
     */
    async getUnreadCount(folder) {
        const emails = await this.getEmailsByFolder(folder);
        return emails.filter(email => !email.isRead).length;
    }
    /**
     * Search emails by text
     */
    async searchEmails(query, folder) {
        const db = await this.ensureDB();
        const emails = folder
            ? await this.getEmailsByFolder(folder)
            : await db.getAll('emails');
        const lowerQuery = query.toLowerCase();
        return emails.filter(email => email.subject.toLowerCase().includes(lowerQuery) ||
            email.body.toLowerCase().includes(lowerQuery) ||
            email.from.name.toLowerCase().includes(lowerQuery) ||
            email.from.email.toLowerCase().includes(lowerQuery));
    }
    // =====================================================
    // FOLDER OPERATIONS
    // =====================================================
    async putFolder(folder) {
        const db = await this.ensureDB();
        await db.put('folders', folder);
    }
    async getFolder(id) {
        const db = await this.ensureDB();
        return db.get('folders', id);
    }
    async getAllFolders() {
        const db = await this.ensureDB();
        return db.getAll('folders');
    }
    async updateFolderCounts(folderId) {
        const folder = await this.getFolder(folderId);
        if (!folder)
            return;
        const totalCount = await this.getEmailCount(folderId);
        const unreadCount = await this.getUnreadCount(folderId);
        await this.putFolder({
            ...folder,
            totalCount,
            unreadCount,
        });
    }
    // =====================================================
    // DRAFT OPERATIONS
    // =====================================================
    async putDraft(draft) {
        const db = await this.ensureDB();
        await db.put('drafts', {
            ...draft,
            modifiedAt: Date.now(),
        });
    }
    async getDraft(id) {
        const db = await this.ensureDB();
        return db.get('drafts', id);
    }
    async getAllDrafts() {
        const db = await this.ensureDB();
        const drafts = await db.getAll('drafts');
        // Sort by modified date descending
        drafts.sort((a, b) => b.modifiedAt - a.modifiedAt);
        return drafts;
    }
    async deleteDraft(id) {
        const db = await this.ensureDB();
        await db.delete('drafts', id);
    }
    // =====================================================
    // SYNC STATE OPERATIONS
    // =====================================================
    async getSyncState(folder) {
        const db = await this.ensureDB();
        return db.get('sync_state', folder);
    }
    async putSyncState(state) {
        const db = await this.ensureDB();
        await db.put('sync_state', state);
    }
    async updateSyncProgress(folder, syncedEmails, totalEmails) {
        const state = await this.getSyncState(folder) || {
            folder,
            lastSyncTimestamp: Date.now(),
            totalEmails: 0,
            syncedEmails: 0,
            isComplete: false,
            isSyncing: true,
        };
        await this.putSyncState({
            ...state,
            syncedEmails,
            totalEmails,
            isComplete: syncedEmails >= totalEmails,
            isSyncing: syncedEmails < totalEmails,
            lastSyncTimestamp: Date.now(),
        });
    }
    // =====================================================
    // CONTACT OPERATIONS
    // =====================================================
    async putContact(contact) {
        const db = await this.ensureDB();
        await db.put('contacts', contact);
    }
    async getContact(email) {
        const db = await this.ensureDB();
        return db.get('contacts', email);
    }
    async getAllContacts() {
        const db = await this.ensureDB();
        return db.getAll('contacts');
    }
    async getRecentContacts(limit = 10) {
        const db = await this.ensureDB();
        const contacts = await db.getAllFromIndex('contacts', 'by-last-contacted');
        // Index returns in ascending order, we want descending
        contacts.reverse();
        return contacts.slice(0, limit);
    }
    /**
     * Update contact from email (increment count, update last contacted)
     */
    async updateContactFromEmail(email) {
        const { from } = email;
        const existing = await this.getContact(from.email);
        if (existing) {
            await this.putContact({
                ...existing,
                name: from.name || existing.name,
                lastContactedAt: new Date(email.date).getTime(),
                emailCount: existing.emailCount + 1,
            });
        }
        else {
            await this.putContact({
                email: from.email,
                name: from.name,
                lastContactedAt: new Date(email.date).getTime(),
                emailCount: 1,
            });
        }
    }
    // =====================================================
    // UTILITY OPERATIONS
    // =====================================================
    /**
     * Clear all emails (useful for sign out or reset)
     */
    async clearAllEmails() {
        const db = await this.ensureDB();
        await db.clear('emails');
    }
    /**
     * Clear all data (complete reset)
     */
    async clearAll() {
        const db = await this.ensureDB();
        await Promise.all([
            db.clear('emails'),
            db.clear('folders'),
            db.clear('drafts'),
            db.clear('sync_state'),
            db.clear('contacts'),
        ]);
    }
    /**
     * Get database statistics
     */
    async getStats() {
        const db = await this.ensureDB();
        const [totalEmails, totalDrafts, totalContacts, folders,] = await Promise.all([
            db.count('emails'),
            db.count('drafts'),
            db.count('contacts'),
            this.getAllFolders(),
        ]);
        const folderStats = await Promise.all(folders.map(async (folder) => ({
            name: folder.name,
            count: await this.getEmailCount(folder.id),
        })));
        // Estimate database size (rough approximation)
        const dbSize = totalEmails * 10; // ~10KB per email average
        return {
            totalEmails,
            totalDrafts,
            totalContacts,
            folders: folderStats,
            dbSize,
        };
    }
    /**
     * Close database connection
     */
    close() {
        if (this.db) {
            this.db.close();
            this.db = null;
            this.initPromise = null;
        }
    }
}
// =====================================================
// SINGLETON INSTANCE
// =====================================================
export const emailDB = new EmailDB();
// Auto-initialize on import
emailDB.init().catch(err => {
    console.error('Failed to auto-initialize EmailDB:', err);
});
